/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Mitch Wilson <mitchell.wilson@deliciousai.com>
 */

import { useMutation } from '@apollo/client'
import { TypedFormik } from '@dai/components'
import {
  Alert,
  Button,
  ButtonProps,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import * as Yup from 'yup'

import { CommonFilters } from '@/components/filters/filters.helpers'
import {
  CreateInvitationInput,
  InvitationFragment,
  SEND_INVITATION,
  SendInvitationMutation,
  SendInvitationMutationVariables,
} from '@/graphql'
import useDaiGroups from '@/hooks/use-dai-groups'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'
import { RemoteSearchableSelect } from '@/pages/components/RemoteSearchableSelect'
import { LocalizationHelpers } from '@/utils/localization.helpers'

function InviteUser({
  onSuccess,
  onCancel,
  otherButtons,
}: {
  onSuccess?: (invitation: InvitationFragment | null | undefined) => void
  onCancel?: () => void
  otherButtons?: ButtonProps[]
}) {
  const { user } = useLoggedInUser()
  const { t } = useT()
  const [selectedStores, setSelectedStores] = useState<
    { label: string; value: string }[]
  >([])

  const [sendInvitation] = useMutation<
    SendInvitationMutation,
    SendInvitationMutationVariables
  >(SEND_INVITATION)

  const {
    groups,
    adminGroup,
    loading: groupsLoading,
    error: groupsError,
  } = useDaiGroups()

  return (
    <Stack>
      <TypedFormik
        initialValues={{
          email: '',
          role: '',
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email(t('inviteUser.validation.email'))
            .max(255)
            .required(t('inviteUser.validation.emailRequired')),
          role: Yup.string().required(t('inviteUser.validation.roleRequired')),
        })}
        onSubmit={async (values, { setStatus, resetForm }) => {
          try {
            if (!user) throw new Error(t('inviteUser.error.userNotFound'))

            const invitationData: CreateInvitationInput = {
              inviterId: user.id,
              email: values.email,
              groupIds: [values.role],
              storeIds: selectedStores.map(store => store.value),
            }
            const result = await sendInvitation({
              variables: {
                data: invitationData,
              },
            })

            setStatus({
              success: true,
              message: t('inviteUser.successMessage'),
            })
            onSuccess?.(result.data?.sendInvitation)
            resetForm()
            setSelectedStores([])
          } catch (error: any) {
            const message =
              error.message || t('inviteUser.error.sendInvitation')
            setStatus({ success: false, message })
          }
        }}
      >
        {({
          status,
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          resetForm,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {status?.message && (
                <Alert severity={status.success ? 'success' : 'warning'}>
                  {status.message}
                </Alert>
              )}
              <TextField
                type="email"
                name="email"
                label={t('inviteUser.emailAddress')}
                value={values.email}
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                onBlur={handleBlur}
                onChange={handleChange}
                required
              />
              <FormControl
                fullWidth
                error={Boolean(touched.role && errors.role)}
                required
              >
                <InputLabel id="role-select-label">
                  {t('inviteUser.role')}
                </InputLabel>
                <Select
                  labelId="role-select-label"
                  name="role"
                  value={values.role}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label={t('inviteUser.role')}
                  fullWidth
                >
                  {groupsLoading ? (
                    <MenuItem value="" disabled>
                      <CircularProgress size={24} />
                    </MenuItem>
                  ) : groupsError ? (
                    <MenuItem value="" disabled>
                      <Typography color="error" paddingLeft={2}>
                        {groupsError.message}
                      </Typography>
                    </MenuItem>
                  ) : (
                    groups.map(group => (
                      <MenuItem key={group.id} value={group.id}>
                        {LocalizationHelpers.localizedGroupName(t, group.name)}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {touched.role && errors.role && (
                  <FormHelperText>{errors.role}</FormHelperText>
                )}
              </FormControl>
              {values.role !== adminGroup?.id && (
                <RemoteSearchableSelect
                  wrapTags
                  query={CommonFilters.StoreFilter(t).options.query}
                  getOptions={CommonFilters.StoreFilter(t).options.getOptions}
                  getVariablesForQuery={
                    CommonFilters.StoreFilter(t).options.getVariablesForQuery
                  }
                  handleChange={newValue => setSelectedStores(newValue)}
                  value={selectedStores}
                  label={t('inviteUser.assignedStores.label')}
                  multiselect
                />
              )}

              <Stack direction={'row'} spacing={2} justifyContent={'flex-end'}>
                {onCancel && (
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      resetForm()
                      setSelectedStores([])
                      onCancel()
                    }}
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                  loadingPosition="end"
                >
                  {t('inviteUser.inviteUser')}
                </Button>
                {otherButtons?.map((button, index) => (
                  <Button key={index} {...button} />
                ))}
              </Stack>
            </Stack>
          </form>
        )}
      </TypedFormik>
    </Stack>
  )
}

export default InviteUser
